import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getUserDetails } from "../api/authService";
import {
  AUTH_STORAGE_KEYS,
  canAccessMenuItem,
  canAccessPath,
  clearAuthStorage,
  forceLogoutFromOtherTab,
  isSessionStale,
  resolveUserRole,
  validateToken,
} from "../auth";
import type {
  AuthContextValue,
  AuthUser,
  UserDetails,
  UserRole,
} from "../auth/types/auth.types";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const role = useMemo(
    () => resolveUserRole(user?.role, userDetails?.role, null),
    [user?.role, userDetails?.role]
  );
  const isAuthenticated = Boolean(token && user);

  // Restore session from localStorage and validate JWT expiration on refresh
  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);
    const validation = validateToken(storedToken);

    if (!storedToken || !storedUser || !validation.isValid) {
      if (validation.isExpired) {
        clearAuthStorage();
      }
      setIsAuthReady(true);
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser) as AuthUser;

      // Prefer exact role from JWT when user_data has no role
      if (validation.roleFromToken && !parsedUser.role) {
        parsedUser.role = validation.roleFromToken;
      }

      setToken(storedToken);
      setUser(parsedUser);
      setUserDetails({
        username: parsedUser.user_username,
        email: parsedUser.email,
        first_name: parsedUser.email.split("@")[0],
        last_name: "",
        role: parsedUser.role,
      });
    } catch {
      clearAuthStorage();
    }

    setIsAuthReady(true);
  }, []);

  // Sync session when another tab logs out or clears localStorage
  useEffect(() => {
    const syncSession = () => {
      if (isSessionStale(token)) {
        setUser(null);
        setToken(null);
        setUserDetails(null);
        forceLogoutFromOtherTab();
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === AUTH_STORAGE_KEYS.TOKEN ||
        event.key === AUTH_STORAGE_KEYS.USER_DATA
      ) {
        syncSession();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncSession();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token]);

  // Enrich profile from backend — also updates exact role from API
  useEffect(() => {
    if (!token || isSessionStale(token)) {
      return;
    }

    getUserDetails()
      .then((res) => {
        const data = res.data?.data?.[0];
        if (!data) {
          return;
        }

        const apiRole = data.role;

        setUserDetails({
          username: data.user_id.username,
          email: data.user_id.email,
          first_name: data.user_id.first_name,
          last_name: data.user_id.last_name,
          role: apiRole,
        });

        // Keep stored user role in sync with backend
        setUser((prev) => (prev ? { ...prev, role: apiRole } : prev));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          return;
        }
        console.warn("User details API failed, using fallback", err);
      });
  }, [token]);

  const login = useCallback((userData: AuthUser, authToken: string) => {
    const validation = validateToken(authToken);
    if (!validation.isValid) {
      console.warn("Login rejected: token is missing or expired");
      return;
    }

    const exactRole = resolveUserRole(
      userData.role,
      null,
      validation.roleFromToken
    );

    const enrichedUser: AuthUser = {
      ...userData,
      role: exactRole ?? userData.role,
    };

    localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, authToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(enrichedUser));

    setToken(authToken);
    setUser(enrichedUser);
    setUserDetails({
      username: enrichedUser.email.split("@")[0],
      email: enrichedUser.email,
      first_name: enrichedUser.email.split("@")[0],
      last_name: "",
      role: enrichedUser.role,
    });
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setToken(null);
    setUserDetails(null);
    window.location.href = "/login";
  }, []);

  const hasRole = useCallback(
    (targetRole: UserRole) => role === targetRole,
    [role]
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]) => (role ? roles.includes(role) : false),
    [role]
  );

  const canAccessRoute = useCallback(
    (path: string) => canAccessPath(path, role),
    [role]
  );

  const canAccessMenu = useCallback(
    (menuId: string) => canAccessMenuItem(menuId, role),
    [role]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      userDetails,
      token,
      role,
      rawRole: role,
      isAuthReady,
      isAuthenticated,
      login,
      logout,
      hasRole,
      hasAnyRole,
      canAccessRoute,
      canAccessMenu,
    }),
    [
      user,
      userDetails,
      token,
      role,
      isAuthReady,
      isAuthenticated,
      login,
      logout,
      hasRole,
      hasAnyRole,
      canAccessRoute,
      canAccessMenu,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
};
